/**
 * The saved-view routes (CS r4b deliverable 122), owner 2.K, since `exit-2a`, unstaged. A view is
 * a named catalogue query of one type; pinning makes a `sidebar_item` of kind `view` (idempotent:
 * a pinned view answers its pin), unpinning is `sidebar.remove`, and removing a view removes its
 * pins. No route names a document: `reference` is `none` throughout.
 */
import { IdParams } from '../dto/common.js';
import { listOf } from '../dto/list.js';
import { SavedViewCreateV1, SavedViewPinV1, SavedViewUpdateV1, SavedViewV1, ViewsQueryV1, } from '../dto/views.js';
import { SidebarItemV1 } from '../dto/workspace.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_VIEW_CREATED, EXAMPLE_VIEW_LOYAL_NPCS, EXAMPLE_VIEW_PIN, EXAMPLE_VIEW_QUEST_BOARD, } from './examples-views.js';
const views = { auth: 'session', status: 'frozen', since: 'exit-2a', owner: '2.K' };
const npcs = { id: EXAMPLE_IDS.viewLoyalNpcs.id };
/** The views, every type's or one type's, in their order: a bounded list. */
export const viewsList = defineRoute({
    id: 'views.list',
    method: 'GET',
    path: '/api/views',
    query: ViewsQueryV1,
    response: { kind: 'json', schema: listOf(SavedViewV1) },
    reference: 'none',
    ...views,
    examples: {
        request: { query: { typeKey: 'npc' } },
        response: { items: [EXAMPLE_VIEW_LOYAL_NPCS], next: null },
    },
});
/** Save a view. A query or column the type cannot answer is 422 `invalid_body`. */
export const viewsCreate = defineRoute({
    id: 'views.create',
    method: 'POST',
    path: '/api/views',
    body: SavedViewCreateV1,
    response: { kind: 'json', schema: SavedViewV1, status: 201 },
    errors: { 422: ['invalid_body'] },
    reference: 'none',
    ...views,
    examples: {
        request: {
            body: {
                name: EXAMPLE_VIEW_CREATED.name,
                typeKey: 'npc',
                layout: 'table',
                query: {
                    filters: [{ column: 'canonStatus', op: 'eq', value: 'draft' }],
                    sort: [{ column: 'title', direction: 'asc' }],
                },
                columns: ['title', 'canonStatus'],
            },
        },
        response: EXAMPLE_VIEW_CREATED,
    },
});
/** Rename a view or change its layout, query or columns, against its `rv`. */
export const viewsUpdate = defineRoute({
    id: 'views.update',
    method: 'PATCH',
    path: '/api/views/:id',
    params: IdParams,
    body: SavedViewUpdateV1,
    response: { kind: 'json', schema: SavedViewV1 },
    errors: { 409: ['row_conflict'], 422: ['invalid_body'] },
    reference: 'none',
    ...views,
    examples: {
        request: { params: npcs, body: { baseRv: 1, name: 'Loyal to the Watch' } },
        response: { ...EXAMPLE_VIEW_LOYAL_NPCS, rv: 2, name: 'Loyal to the Watch' },
        consumes: true,
    },
});
/** Delete a view; its pins go with it. */
export const viewsRemove = defineRoute({
    id: 'views.remove',
    method: 'DELETE',
    path: '/api/views/:id',
    params: IdParams,
    response: { kind: 'empty' },
    reference: 'none',
    ...views,
    examples: {
        request: { params: { id: EXAMPLE_VIEW_QUEST_BOARD.id } },
        response: null,
        consumes: true,
    },
});
/** Pin a view to the sidebar; a view already pinned answers its pin. */
export const viewsPin = defineRoute({
    id: 'views.pin',
    method: 'PUT',
    path: '/api/views/:id/pin',
    params: IdParams,
    body: SavedViewPinV1,
    response: { kind: 'json', schema: SidebarItemV1 },
    reference: 'none',
    ...views,
    examples: { request: { params: npcs, body: {} }, response: EXAMPLE_VIEW_PIN },
});
//# sourceMappingURL=views.js.map